//Consider dynamically importing package
import * as Amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { Experiment, } from '@amplitude/experiment-js-client';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { getAmplitudeApiKey, getAppVersion, isAmplitudeGuidesAndSurveysEnabled, } from '#services';
// Autocapture ignores PII_MASK_CLASS; it masks text under the first attribute and the listed attributes under the second
const PII_MASK_ATTRIBUTE = 'data-amp-mask';
const PII_MASK_ATTRIBUTES_ATTRIBUTE = 'data-amp-mask-attributes';
// id and class can't be masked by Autocapture
const AUTOCAPTURE_MASKED_ATTRIBUTES = [
    'alt',
    'aria-label',
    'href',
    'placeholder',
    'title',
];
// <body>, not <html>: the attribute masking only checks ancestors below <html>. <title> stays unmasked for Page Title
const maskAutocapturedData = () => {
    document.body.setAttribute(PII_MASK_ATTRIBUTE, '');
    document.body.setAttribute(PII_MASK_ATTRIBUTES_ATTRIBUTE, AUTOCAPTURE_MASKED_ATTRIBUTES.join(','));
};
// This list is derived from Amplitude types. Below is a copy of their description:
/**
 * List of CSS selectors to allow auto tracking on.
 * When provided, allow elements matching any selector to be tracked.
 * Default is ['a', 'button', 'input', 'select', 'textarea', 'label', '[data-amp-default-track]', '.amp-default-track'].
 */
const DEFAULT_CSS_SELECTOR_ALLOWLIST = [
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    '[data-amp-default-track]',
    '.amp-default-track',
];
// The OneTrust category the AMP_* cookies are classified under (Targeting)
const ONETRUST_AMPLITUDE_GROUP = 'C0004';
const ONETRUST_GROUPS_UPDATED_EVENT = 'OneTrustGroupsUpdated';
// The SDK names its cookies AMP_<first 10 key chars> and AMP_MKTG_<first 10 key chars>
const AMPLITUDE_COOKIE_PREFIXES = ['AMP_', 'AMP_MKTG_'];
const AMPLITUDE_COOKIE_KEY_LENGTH = 10;
const DETACHED_COOKIE_STORAGE = {
    isEnabled: () => Promise.resolve(true),
    get: () => Promise.resolve(undefined),
    getRaw: () => Promise.resolve(undefined),
    set: () => Promise.resolve(),
    remove: () => Promise.resolve(),
    reset: () => Promise.resolve(),
};
let experimentClient;
let appVersion;
let isStarted = false;
let isTracking = false;
let whenInitialized = Promise.resolve();
let consentedPlugins = [];
let browserConfig;
let cookieStorage;
const hasAmplitudeCookieConsent = () => window.OnetrustActiveGroups?.includes(`,${ONETRUST_AMPLITUDE_GROUP},`) ??
    false;
// Gives access to the SDK's cookie storage, which has no public setter outside a plugin
const cookieStoragePlugin = {
    name: 'lucid-cookie-consent',
    type: 'enrichment',
    setup: (config) => {
        browserConfig = config;
        cookieStorage = config.cookieStorage;
        return Promise.resolve();
    },
    execute: (event) => Promise.resolve(event),
};
const addConsentedPlugins = (sessionReplaySampleRate) => {
    consentedPlugins = [
        ...(sessionReplaySampleRate === undefined
            ? []
            : [
                sessionReplayPlugin({
                    sampleRate: sessionReplaySampleRate,
                    // Session Replay masks only the listed attributes, and only under the data-amp-mask set on <body>
                    privacyConfig: {
                        maskAttributes: AUTOCAPTURE_MASKED_ATTRIBUTES,
                    },
                }),
            ]),
        ...(isAmplitudeGuidesAndSurveysEnabled() ? [engagementPlugin()] : []),
    ];
    consentedPlugins.forEach((plugin) => Amplitude.add(plugin));
};
const removeConsentedPlugins = () => {
    consentedPlugins.forEach(({ name }) => name !== undefined && Amplitude.remove(name));
    consentedPlugins = [];
};
// The SDK rewrites its cookies on every event even when opted out, so they are detached and deleted
const stopTracking = (apiKey) => {
    Amplitude.setOptOut(true);
    removeConsentedPlugins();
    if (browserConfig && cookieStorage) {
        const persistentStorage = cookieStorage;
        const cookieKey = apiKey.slice(0, AMPLITUDE_COOKIE_KEY_LENGTH);
        browserConfig.cookieStorage = DETACHED_COOKIE_STORAGE;
        AMPLITUDE_COOKIE_PREFIXES.forEach((prefix) => void persistentStorage.remove(`${prefix}${cookieKey}`));
    }
};
const resumeTracking = (sessionReplaySampleRate) => {
    if (browserConfig && cookieStorage) {
        browserConfig.cookieStorage = cookieStorage;
    }
    Amplitude.setOptOut(false);
    addConsentedPlugins(sessionReplaySampleRate);
};
const startAmplitude = ({ apiKey, requiresCookieConsent, sessionReplaySampleRate, }) => {
    isStarted = true;
    isTracking = true;
    whenInitialized = Amplitude.init(apiKey, {
        // Populates the Amplitude "Version" event property
        ...(appVersion && { appVersion }),
        // A withdrawal on an earlier visit may have persisted optOut
        ...(requiresCookieConsent && { optOut: false }),
        defaultTracking: true,
        autocapture: {
            // Adds the full URL, including guest link secrets, to every event
            pageUrlEnrichment: false,
            elementInteractions: {
                cssSelectorAllowlist: [
                    ...DEFAULT_CSS_SELECTOR_ALLOWLIST,
                    // adding the ability to select by [data-testid] as it's missing from the default list!
                    '[data-testid]',
                ],
            },
        },
    }).promise.then(() => undefined);
    if (requiresCookieConsent) {
        Amplitude.add(cookieStoragePlugin);
    }
    addConsentedPlugins(sessionReplaySampleRate);
};
const applyCookieConsent = (options) => {
    const hasConsent = hasAmplitudeCookieConsent();
    if (hasConsent === isTracking) {
        return;
    }
    if (!isStarted) {
        startAmplitude(options);
        return;
    }
    isTracking = hasConsent;
    void whenInitialized.then(() => hasConsent
        ? resumeTracking(options.sessionReplaySampleRate)
        : stopTracking(options.apiKey));
};
export const initAmplitude = ({ appVersion: appVersionOverride, requiresCookieConsent = false, } = {}) => {
    const amplitudeApiKey = getAmplitudeApiKey();
    appVersion =
        appVersionOverride === null
            ? undefined
            : (appVersionOverride ?? getAppVersion());
    if (amplitudeApiKey) {
        try {
            maskAutocapturedData();
            const sampleRate = Number.parseFloat(process.env.AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE ?? '');
            // decimal bigger than 0 and <= 1
            const hasValidSampleRate = sampleRate > 0 && sampleRate <= 1;
            const startOptions = {
                apiKey: amplitudeApiKey,
                requiresCookieConsent,
                sessionReplaySampleRate: hasValidSampleRate
                    ? sampleRate
                    : undefined,
            };
            if (requiresCookieConsent) {
                applyCookieConsent(startOptions);
                // Fired by OneTrust once it loads and on every consent change
                window.addEventListener(ONETRUST_GROUPS_UPDATED_EVENT, () => applyCookieConsent(startOptions));
            }
            else {
                startAmplitude(startOptions);
            }
            // Not coupled to analytics: feature flags must load without cookie consent
            experimentClient = Experiment.initialize(amplitudeApiKey, {
                userProvider: {
                    getUser: () => {
                        const deviceId = Amplitude.getDeviceId();
                        return deviceId ? { device_id: deviceId } : {};
                    },
                },
                exposureTrackingProvider: {
                    track: (exposure) => {
                        if (isStarted) {
                            void Amplitude.track('$exposure', exposure);
                        }
                    },
                },
            });
        }
        catch (error) {
            // eslint-disable-next-line no-console -- log if 3rd party fails
            console.error(error);
        }
    }
};
export const isAmplitudeStarted = () => isStarted;
export const getAmplitudeExperimentClient = () => experimentClient;
export const getAmplitudeAppVersion = () => appVersion;
export * as Amplitude from '@amplitude/analytics-browser';
